import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { AlertTriangle, Receipt } from "lucide-react";
import { sanitizeName, sanitizeText } from "@/lib/sanitize";

interface BillingData {
  id?: string;
  full_name: string;
  address_street: string;
  address_zip: string;
  address_city: string;
  address_province: string;
  address_country: string;
  fiscal_code: string;
  vat_number: string;
}

interface BillingDataModalProps {
  open: boolean;
  onSuccess?: () => void;
  editMode?: boolean;
  onClose?: () => void;
  existingData?: BillingData | null;
}

const ITALIAN_PROVINCES = [
  "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO", "BR", "BS", "BT", "BZ", "CA",
  "CB", "CE", "CH", "CI", "CL", "CN", "CO", "CR", "CS", "CT", "CZ", "EN", "FC", "FE", "FG", "FI", "FM", "FR", "GE", "GO",
  "GR", "IM", "IS", "KR", "LC", "LE", "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT", "NA", "NO",
  "NU", "OG", "OR", "OT", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ", "RA", "RC", "RE",
  "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SU", "SV", "TA", "TE", "TN", "TO", "TP", "TR", "TS",
  "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VS", "VT", "VV"
];

const COUNTRY_OPTIONS = [
  { value: "IT", label: "Italia" },
  { value: "AT", label: "Austria" },
  { value: "BE", label: "Belgio" },
  { value: "BG", label: "Bulgaria" },
  { value: "HR", label: "Croazia" },
  { value: "CY", label: "Cipro" },
  { value: "CZ", label: "Repubblica Ceca" },
  { value: "DK", label: "Danimarca" },
  { value: "EE", label: "Estonia" },
  { value: "FI", label: "Finlandia" },
  { value: "FR", label: "Francia" },
  { value: "DE", label: "Germania" },
  { value: "GR", label: "Grecia" },
  { value: "HU", label: "Ungheria" },
  { value: "IE", label: "Irlanda" },
  { value: "LV", label: "Lettonia" },
  { value: "LT", label: "Lituania" },
  { value: "LU", label: "Lussemburgo" },
  { value: "MT", label: "Malta" },
  { value: "NL", label: "Paesi Bassi" },
  { value: "PL", label: "Polonia" },
  { value: "PT", label: "Portogallo" },
  { value: "RO", label: "Romania" },
  { value: "SK", label: "Slovacchia" },
  { value: "SI", label: "Slovenia" },
  { value: "ES", label: "Spagna" },
  { value: "SE", label: "Svezia" },
  { value: "CH", label: "Svizzera" },
  { value: "GB", label: "Regno Unito" },
  { value: "OTHER", label: "Altro" },
];

export const BillingDataModal = ({ 
  open, 
  onSuccess, 
  editMode = false, 
  onClose,
  existingData 
}: BillingDataModalProps) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<BillingData>({
    full_name: "",
    address_street: "",
    address_zip: "",
    address_city: "",
    address_province: "",
    address_country: "IT",
    fiscal_code: "",
    vat_number: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isItaly = formData.address_country === "IT";

  useEffect(() => {
    if (existingData) {
      setFormData({
        id: existingData.id,
        full_name: existingData.full_name || "",
        address_street: existingData.address_street || "",
        address_zip: existingData.address_zip || "",
        address_city: existingData.address_city || "",
        address_province: existingData.address_province || "",
        address_country: existingData.address_country || "IT",
        fiscal_code: existingData.fiscal_code || "",
        vat_number: existingData.vat_number || "",
      });
    }
  }, [existingData]);

  const validateFiscalCode = (code: string): boolean => {
    if (!code) return true;
    if (!isItaly) return true; // No regex for non-IT
    const regex = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i;
    return regex.test(code);
  };

  const validateVatNumber = (vat: string): boolean => {
    if (!vat) return true;
    if (!isItaly) return true; // No regex for non-IT
    const regex = /^[0-9]{11}$/;
    return regex.test(vat);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = "Nome e cognome sono obbligatori";
    }
    if (!formData.address_street.trim()) {
      newErrors.address_street = "Via e numero civico sono obbligatori";
    }

    // ZIP validation
    if (!formData.address_zip.trim()) {
      newErrors.address_zip = "CAP obbligatorio";
    } else if (isItaly && !/^\d{5}$/.test(formData.address_zip)) {
      newErrors.address_zip = "CAP non valido (5 cifre)";
    } else if (!isItaly && !/^[A-Z0-9\s-]{2,10}$/i.test(formData.address_zip)) {
      newErrors.address_zip = "CAP/ZIP non valido";
    }

    if (!formData.address_city.trim()) {
      newErrors.address_city = "Città obbligatoria";
    }

    // Province validation
    if (!formData.address_province.trim()) {
      newErrors.address_province = isItaly ? "Provincia obbligatoria" : "Provincia/Stato obbligatorio";
    } else if (isItaly && !ITALIAN_PROVINCES.includes(formData.address_province.toUpperCase())) {
      newErrors.address_province = "Sigla provincia non valida (es. MI, RM)";
    } else if (!isItaly && formData.address_province.trim().length > 10) {
      newErrors.address_province = "Max 10 caratteri";
    }

    // Fiscal code / VAT: required at least one only for Italy
    if (isItaly) {
      if (!formData.fiscal_code.trim() && !formData.vat_number.trim()) {
        newErrors.fiscal_code = "Inserisci codice fiscale o partita IVA";
        newErrors.vat_number = "Inserisci codice fiscale o partita IVA";
      } else {
        if (formData.fiscal_code.trim() && !validateFiscalCode(formData.fiscal_code)) {
          newErrors.fiscal_code = "Codice fiscale non valido (16 caratteri)";
        }
        if (formData.vat_number.trim() && !validateVatNumber(formData.vat_number)) {
          newErrors.vat_number = "Partita IVA non valida (11 cifre)";
        }
      }
    } else {
      // Non-IT: optional, no strict validation
      if (formData.fiscal_code.trim() && !validateFiscalCode(formData.fiscal_code)) {
        newErrors.fiscal_code = "Codice fiscale non valido";
      }
      if (formData.vat_number.trim() && !validateVatNumber(formData.vat_number)) {
        newErrors.vat_number = "Partita IVA non valida";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!user) return;

    setIsSubmitting(true);

    try {
      const dataToSave = {
        user_id: user.id,
        full_name: sanitizeName(formData.full_name.trim()),
        address_street: sanitizeText(formData.address_street.trim()),
        address_zip: formData.address_zip.trim(),
        address_city: sanitizeText(formData.address_city.trim()),
        address_province: formData.address_province.toUpperCase().trim(),
        address_country: formData.address_country.trim(),
        fiscal_code: formData.fiscal_code.trim().toUpperCase() || null,
        vat_number: formData.vat_number.trim() || null,
      };

      if (existingData?.id) {
        const { error } = await supabase
          .from("billing_data")
          .update(dataToSave)
          .eq("id", existingData.id);

        if (error) throw error;
        toast.success("Dati fiscali aggiornati con successo");
      } else {
        const { error } = await supabase
          .from("billing_data")
          .insert(dataToSave);

        if (error) throw error;
        toast.success("Dati fiscali salvati con successo");
      }

      onSuccess?.();
    } catch (error: any) {
      console.error("Error saving billing data:", error);
      toast.error("Errore nel salvataggio dei dati fiscali");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof BillingData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const getCountryLabel = (code: string) => {
    return COUNTRY_OPTIONS.find(c => c.value === code)?.label || code;
  };

  return (
    <Dialog open={open} onOpenChange={editMode ? onClose : undefined}>
      <DialogContent 
        className="sm:max-w-lg border-[3px] border-dark shadow-brutal bg-background"
        onPointerDownOutside={editMode ? undefined : (e) => e.preventDefault()}
        onEscapeKeyDown={editMode ? undefined : (e) => e.preventDefault()}
        hideCloseButton={!editMode}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary border-[3px] border-dark flex items-center justify-center">
              <Receipt className="w-5 h-5 text-dark" />
            </div>
            <DialogTitle className="font-heading text-xl">
              {editMode ? "Modifica dati fiscali" : "Dati per la fatturazione"}
            </DialogTitle>
          </div>
          {!editMode && (
            <DialogDescription className="font-body text-muted-foreground">
              <span className="flex items-center gap-2 text-warning-foreground bg-warning/20 p-3 rounded border-2 border-dark mt-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Per emettere la fattura abbiamo bisogno dei tuoi dati fiscali. Compila tutti i campi per continuare.
              </span>
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="full_name" className="font-body font-semibold">
              Nome e Cognome / Ragione Sociale *
            </Label>
            <BrutalInput
              id="full_name"
              value={formData.full_name}
              onChange={(e) => handleChange("full_name", e.target.value)}
              placeholder="Mario Rossi"
              className={errors.full_name ? "border-destructive" : ""}
            />
            {errors.full_name && (
              <p className="text-destructive text-sm mt-1">{errors.full_name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="address_street" className="font-body font-semibold">
              Indirizzo (Via e numero civico) *
            </Label>
            <BrutalInput
              id="address_street"
              value={formData.address_street}
              onChange={(e) => handleChange("address_street", e.target.value)}
              placeholder="Via Roma 123"
              className={errors.address_street ? "border-destructive" : ""}
            />
            {errors.address_street && (
              <p className="text-destructive text-sm mt-1">{errors.address_street}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="address_zip" className="font-body font-semibold">
                CAP *
              </Label>
              <BrutalInput
                id="address_zip"
                value={formData.address_zip}
                onChange={(e) => handleChange("address_zip", e.target.value)}
                placeholder={isItaly ? "00100" : "1170"}
                maxLength={isItaly ? 5 : 10}
                className={errors.address_zip ? "border-destructive" : ""}
              />
              {errors.address_zip && (
                <p className="text-destructive text-sm mt-1">{errors.address_zip}</p>
              )}
            </div>
            <div>
              <Label htmlFor="address_city" className="font-body font-semibold">
                Città *
              </Label>
              <BrutalInput
                id="address_city"
                value={formData.address_city}
                onChange={(e) => handleChange("address_city", e.target.value)}
                placeholder={isItaly ? "Roma" : "Wien"}
                className={errors.address_city ? "border-destructive" : ""}
              />
              {errors.address_city && (
                <p className="text-destructive text-sm mt-1">{errors.address_city}</p>
              )}
            </div>
            <div>
              <Label htmlFor="address_province" className="font-body font-semibold">
                {isItaly ? "Prov. *" : "Prov./Stato *"}
              </Label>
              <BrutalInput
                id="address_province"
                value={formData.address_province}
                onChange={(e) => handleChange("address_province", isItaly ? e.target.value.toUpperCase() : e.target.value)}
                placeholder={isItaly ? "RM" : "W"}
                maxLength={isItaly ? 2 : 10}
                className={errors.address_province ? "border-destructive" : ""}
              />
              {errors.address_province && (
                <p className="text-destructive text-sm mt-1">{errors.address_province}</p>
              )}
            </div>
          </div>

          {/* Country selector */}
          <div>
            <Label htmlFor="address_country" className="font-body font-semibold">
              Stato *
            </Label>
            <Select
              value={formData.address_country}
              onValueChange={(value) => handleChange("address_country", value)}
            >
              <SelectTrigger className="border-[3px] border-dark bg-card font-body">
                <SelectValue placeholder="Seleziona stato" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map((country) => (
                  <SelectItem key={country.value} value={country.value}>
                    {country.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-t-2 border-dark pt-4 mt-4">
            <p className="font-body text-sm text-muted-foreground mb-3">
              {isItaly
                ? "Inserisci almeno uno tra Codice Fiscale e Partita IVA"
                : "Codice fiscale e partita IVA sono opzionali per residenti all'estero"}
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fiscal_code" className="font-body font-semibold">
                  Codice Fiscale {isItaly ? "" : "(opzionale)"}
                </Label>
                <BrutalInput
                  id="fiscal_code"
                  value={formData.fiscal_code}
                  onChange={(e) => handleChange("fiscal_code", e.target.value.toUpperCase())}
                  placeholder="RSSMRA80A01H501U"
                  maxLength={16}
                  className={errors.fiscal_code ? "border-destructive" : ""}
                />
                {errors.fiscal_code && (
                  <p className="text-destructive text-sm mt-1">{errors.fiscal_code}</p>
                )}
              </div>
              <div>
                <Label htmlFor="vat_number" className="font-body font-semibold">
                  Partita IVA {isItaly ? "" : "(opzionale)"}
                </Label>
                <BrutalInput
                  id="vat_number"
                  value={formData.vat_number}
                  onChange={(e) => handleChange("vat_number", isItaly ? e.target.value.replace(/\D/g, "") : e.target.value)}
                  placeholder={isItaly ? "12345678901" : "ATU12345678"}
                  maxLength={isItaly ? 11 : 20}
                  className={errors.vat_number ? "border-destructive" : ""}
                />
                {errors.vat_number && (
                  <p className="text-destructive text-sm mt-1">{errors.vat_number}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            {editMode && onClose && (
              <BrutalButton 
                type="button" 
                variant="outline" 
                onClick={onClose}
              >
                Annulla
              </BrutalButton>
            )}
            <BrutalButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Salvataggio..." : "Salva dati fiscali"}
            </BrutalButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
